import { useState, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Camera, Upload, FileText, FileImage, Loader2, Check, X, Plus, Search, Lock, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFileUpload } from "@/hooks/useFileUpload";
import { useServiceLogs } from "@/hooks/useServiceLogs";
import { useProviders } from "@/hooks/useProviders";
import { useAppliances } from "@/hooks/useAppliances";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { useSubscription } from "@/hooks/useSubscription";
import { Switch } from "@/components/ui/switch";
import AddProviderDialog from "./AddProviderDialog";
import UpgradePlanSheet from "./UpgradePlanSheet";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const SmartCaptureSheet = ({ open, onOpenChange, applianceId, onSuccess }) => {
    const { activeProperty } = usePropertyContext();
    const { uploading, uploadFile, capturePhoto, selectFile } = useFileUpload();
    const { serviceLogs: allServiceLogs, addServiceLog } = useServiceLogs(activeProperty?.id);
    const { providers, refetch: refetchProviders } = useProviders();
    const { appliances } = useAppliances(activeProperty?.id);
    const { isFeatureLocked, plan } = useSubscription();

    const [uploadedFile, setUploadedFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [automatedFollowup, setAutomatedFollowup] = useState(true);
    const [isAddProviderOpen, setIsAddProviderOpen] = useState(false);
    const [showUpgradeSheet, setShowUpgradeSheet] = useState(false);
    const [providerSearch, setProviderSearch] = useState("");

    // Limit Logic: Check unique appliances that ALREADY have logs (Max 2)
    const uniqueApplianceIdsWithLogs = useMemo(() => {
        if (!allServiceLogs) return new Set();
        return new Set(allServiceLogs.map(l => l.appliance_id).filter(Boolean));
    }, [allServiceLogs]);

    const isNewAppliance = useMemo(() => {
        return applianceId && !uniqueApplianceIdsWithLogs.has(applianceId);
    }, [applianceId, uniqueApplianceIdsWithLogs]);

    const isTotalAppliancesLimitReached = useMemo(() => {
        return isNewAppliance && isFeatureLocked('maintenance_appliance_limit', uniqueApplianceIdsWithLogs.size);
    }, [isNewAppliance, isFeatureLocked, uniqueApplianceIdsWithLogs.size]);

    const isLimitReached = isTotalAppliancesLimitReached;
    const [formData, setFormData] = useState({
        providerId: "",
        date: new Date().toISOString().split("T")[0],
        description: "",
        cost: "",
    });

    // Find the category of the current appliance to prioritize providers
    const applianceCategory = useMemo(() => {
        if (!applianceId || !appliances) return null;
        return appliances.find(a => a.id === applianceId)?.category;
    }, [applianceId, appliances]);

    // Filter and Sort Providers:
    // 1. Prioritize those in the appliance category
    // 2. Support soft search matching
    const filteredProviders = useMemo(() => {
        let list = [...providers];
        
        if (providerSearch.trim()) {
            const search = providerSearch.toLowerCase();
            list = list.filter(p => 
                p.name.toLowerCase().includes(search) || 
                (p.category && p.category.toLowerCase().includes(search))
            );
        }

        return list.sort((a, b) => {
            // Prioritize matching category
            const aMatch = a.category === applianceCategory ? 1 : 0;
            const bMatch = b.category === applianceCategory ? 1 : 0;
            if (aMatch !== bMatch) return bMatch - aMatch;
            
            // Then sort alphabetically
            return a.name.localeCompare(b.name);
        });
    }, [providers, applianceCategory, providerSearch]);

    const handleCameraCapture = async () => {
        const file = await capturePhoto();
        if (file) {
            const result = await uploadFile(file, "service-receipts");
            if (result) {
                setUploadedFile(result);
            }
        }
    };

    const handleFileSelect = async () => {
        const file = await selectFile();
        if (file) {
            const result = await uploadFile(file, "service-receipts");
            if (result) {
                setUploadedFile(result);
            }
        }
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
    };

    const handleSave = async () => {
        if (!formData.description.trim()) return;

        setSaving(true);
        const success = await addServiceLog({
            applianceId,
            providerId: formData.providerId || undefined,
            propertyId: activeProperty?.id,
            date: formData.date,
            description: formData.description.trim(),
            cost: formData.cost ? parseFloat(formData.cost) : undefined,
            invoiceUrl: uploadedFile?.url,
            automatedFollowup,
        });

        setSaving(false);

        if (success) {
            resetForm();
            onOpenChange(false);
            onSuccess?.();
        }
    };

    const resetForm = () => {
        setUploadedFile(null);
        setFormData({
            providerId: "",
            date: new Date().toISOString().split("T")[0],
            description: "",
            cost: "",
        });
        setAutomatedFollowup(true);
    };

    const handleClose = (open) => {
        if (!open) resetForm();
        onOpenChange(open);
    };

    const handleFocus = (e) => {
        setTimeout(() => {
            e.target.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 350);
    };

    const isImage = uploadedFile?.type.startsWith("image/");
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent
                side="bottom"
                className="max-h-[85vh] rounded-t-3xl flex flex-col p-0"
                style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
            >
                <SheetHeader className="pb-4 flex-shrink-0 px-6 pt-8">
                    <SheetTitle className="text-xl font-bold text-foreground">
                        Log Service
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 space-y-4 pb-4 px-6">
                    {/* Upload Options */}
                    <div className="space-y-3">
                        <Label className="text-sm font-medium text-foreground">
                            Upload Receipt or Invoice
                        </Label>

                        {!uploadedFile ? (
                            <div
                                className={cn(
                                    "w-full border-2 border-dashed border-border rounded-xl",
                                    "flex flex-col items-center justify-center gap-3 py-6",
                                    "transition-colors",
                                    uploading ? "opacity-50 pointer-events-none bg-muted/20" : "hover:border-primary/50 hover:bg-accent/50"
                                )}
                            >
                                {uploading ? (
                                    <div className="flex flex-col items-center gap-2">
                                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                        <span className="text-sm text-muted-foreground">Uploading...</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex gap-4">
                                            <button
                                                type="button"
                                                onClick={handleCameraCapture}
                                                className="flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-accent/50 transition-colors"
                                            >
                                                <div className="p-3 rounded-full bg-primary/10">
                                                    <Camera className="w-6 h-6 text-primary" />
                                                </div>
                                                <span className="text-xs text-muted-foreground">Take Photo</span>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={handleFileSelect}
                                                className="flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-accent/50 transition-colors"
                                            >
                                                <div className="p-3 rounded-full bg-primary/10">
                                                    <Upload className="w-6 h-6 text-primary" />
                                                </div>
                                                <span className="text-xs text-muted-foreground">Upload File</span>
                                            </button>
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                            Supports images and PDF files
                                        </span>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="relative w-full p-4 bg-muted/30 rounded-xl border border-border">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                                        {isImage ? (
                                            <FileImage className="w-5 h-5 text-primary" />
                                        ) : (
                                            <FileText className="w-5 h-5 text-primary" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-foreground truncate">
                                            {uploadedFile.name}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatFileSize(uploadedFile.size)} • Uploaded
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <div className="p-1 bg-emerald-500 rounded-full">
                                            <Check className="w-3 h-3 text-primary-foreground" />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleRemoveFile}
                                            className="p-1 hover:bg-muted rounded-full transition-colors"
                                        >
                                            <X className="w-4 h-4 text-muted-foreground" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-4">
                    {/* Provider Selection */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label>Service Provider</Label>
                            {applianceCategory && (
                                <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                                    Prioritizing {applianceCategory}
                                </span>
                            )}
                        </div>
                        
                        <Select
                            value={formData.providerId}
                            onValueChange={(value) => {
                                if (value === "add-new") {
                                    setIsAddProviderOpen(true);
                                } else {
                                    setFormData(prev => ({ ...prev, providerId: value }));
                                }
                            }}
                        >
                            <SelectTrigger className="rounded-xl">
                                <SelectValue placeholder="Select provider (optional)" />
                            </SelectTrigger>
                            <SelectContent className="bg-popover border border-border z-[100] max-h-[300px]">
                                <div className="px-2 py-2 sticky top-0 bg-popover z-10 border-b mb-1">
                                    <div className="relative">
                                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                        <Input
                                            placeholder="Search all providers..."
                                            value={providerSearch}
                                            onChange={(e) => setProviderSearch(e.target.value)}
                                            className="h-8 pl-8 text-xs rounded-lg"
                                            onClick={(e) => e.stopPropagation()}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                </div>
                                
                                <div className="overflow-y-auto">
                                    {filteredProviders.length === 0 ? (
                                        <div className="py-6 text-center text-xs text-muted-foreground">
                                            No providers found matching "{providerSearch}"
                                        </div>
                                    ) : (
                                        <>
                                            {/* Matching Category Group */}
                                            {filteredProviders.some(p => p.category === applianceCategory) && !providerSearch && (
                                                <div className="px-2 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                                                    Recommended for {applianceCategory}
                                                </div>
                                            )}
                                            
                                            {filteredProviders.map((provider) => (
                                                <SelectItem key={provider.id} value={provider.id}>
                                                    <div className="flex items-center justify-between w-full gap-2">
                                                        <span>{provider.name}</span>
                                                        <span className="text-[10px] opacity-50 italic">
                                                            {provider.category}
                                                        </span>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </>
                                    )}
                                    
                                    <div className="border-t mt-1 pt-1">
                                        <SelectItem value="add-new" className="text-primary font-bold">
                                            <div className="flex items-center gap-2">
                                                <Plus className="w-4 h-4 ml-1 mr-2" />
                                                Add New Provider
                                            </div>
                                        </SelectItem>
                                    </div>
                                </div>
                            </SelectContent>
                        </Select>
                    </div>

                        <div className="space-y-2">
                            <Label htmlFor="service-date">Date</Label>
                            <Input
                                id="service-date"
                                type="date"
                                value={formData.date}
                                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                                onFocus={handleFocus}
                                className="rounded-xl w-full"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="service-description">Work Performed *</Label>
                            <Textarea
                                id="service-description"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                onFocus={handleFocus}
                                placeholder="Describe the service work performed"
                                className="rounded-xl resize-none"
                                rows={3}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="service-cost">Cost (R)</Label>
                            <Input
                                id="service-cost"
                                type="number"
                                inputMode="decimal"
                                value={formData.cost}
                                onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                                onFocus={handleFocus}
                                placeholder="0.00"
                                className="rounded-xl"
                            />
                        </div>
                    </div>

                    {/* Automated Follow-up Toggle */}
                    <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl border border-border">
                        <div className="flex-1 pr-4">
                            <p className="text-sm font-medium text-foreground">Automated Follow-up</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Get reminded when next service is due
                            </p>
                        </div>
                        <Switch
                            checked={automatedFollowup}
                            onCheckedChange={setAutomatedFollowup}
                        />
                    </div>
                </div>

                <div className="flex-shrink-0 pt-2 px-6">
                    {isLimitReached ? (
                        <div className="space-y-3">
                            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                                <div className="space-y-1">
                                    <p className="text-sm font-bold text-amber-900">Service Log Limit Reached</p>
                                    <p className="text-xs text-amber-700 leading-relaxed">
                                        Free tier allows logging service history on max 2 appliances. Upgrade to cover your full home portfolio.
                                    </p>
                                </div>
                            </div>
                            <Button
                                onClick={() => setShowUpgradeSheet(true)}
                                className="w-full rounded-xl py-6 bg-amber-500 hover:bg-amber-600 text-white font-bold gap-2 active-scale transition-all"
                            >
                                <Lock className="w-4 h-4" />
                                Upgrade to Log More
                            </Button>
                        </div>
                    ) : (
                        <Button
                            onClick={handleSave}
                            className="w-full rounded-xl py-5"
                            disabled={!formData.description.trim() || saving}
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Saving...
                                </>
                            ) : (
                                "Save Service Log"
                            )}
                        </Button>
                    )}
                </div>
                <AddProviderDialog
                    open={isAddProviderOpen}
                    onOpenChange={setIsAddProviderOpen}
                    onSuccess={(newProvider) => {
                        refetchProviders();
                        setFormData(prev => ({ ...prev, providerId: newProvider.id }));
                    }}
                />
                <UpgradePlanSheet
                    open={showUpgradeSheet}
                    onOpenChange={setShowUpgradeSheet}
                    currentPlan={plan}
                />
            </SheetContent>
        </Sheet>
    );
};

export default SmartCaptureSheet;
